/**
 * <WaitingText> — une petite attente, dite en mots et soulignée en boucle (atom).
 *
 * Pour ce qu'aucun squelette ne peut prendre : un écran d'attente d'une seconde (« Connexion en
 * cours… »), une tuile qui s'ouvre. Les mots ne bougent pas — « ne bougent jamais : le texte » ; c'est
 * le surligneur du livre qui les souligne de gauche à droite, s'efface, et recommence. Le même geste
 * que le trait des squelettes, à l'échelle d'une ligne : une seule façon d'attendre dans l'app.
 *
 * Jamais une roue : le livre range le spinner dans ses interdits. Le bouton qui envoie garde la
 * sienne (`<Button loading>`) — l'action en cours se lit sur le bouton qu'on vient de toucher.
 *
 * En mouvement réduit, le trait est posé, immobile.
 *
 * Le trait est ancré au BAS des mots, pas à une fraction de leur boîte : sur deux lignes, il souligne
 * la dernière au lieu de couvrir le bloc. Sur la nuit, il reste un trait SOUS les mots : la bande du
 * surligneur de nuit couvre le mot et le passe en marine, ce qui le rendrait illisible là où le
 * trait n'est pas encore passé.
 */
import React, { useEffect, useRef, useState } from "react"
import { Animated, Easing, Platform, StyleSheet as RNStyleSheet, View, type LayoutChangeEvent } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { useIsDarkTheme } from "../../hooks/useIsDarkTheme"
import { useReduceMotion } from "../../hooks/useReduceMotion"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { highlighter } from "../../theme/tokens/effects"
import { type FontFamilyKey, type FontSizeKey } from "../../theme/tokens/typography"
import { type Align, alignMap } from "../containers/layout"
import { Text } from "../typography/Text"

type WaitingTextAlign = Extract<Align, "center" | "start">

export type WaitingTextProps = {
    /** Les mots de l'attente — ce qui se passe, pas « Chargement ». */
    children: string
    size?: FontSizeKey
    weight?: FontFamilyKey
    color?: SemanticColorKey
    /** Lignes au plus — 1 par défaut : une attente se dit en quelques mots. */
    numberOfLines?: number
    /**
     * `start` sous un champ ou dans un encart dont les textes partent du bord : l'attente s'y range
     * comme ses voisins. `center` (défaut) seule sur un écran ou sous une échelle centrée.
     */
    align?: WaitingTextAlign
    /**
     * `false` quand l'élément qui le porte annonce déjà l'attente (une tuile `busy` qui porte le même
     * nom) : les mots restent visibles, le lecteur d'écran ne les entend qu'une fois.
     */
    announce?: boolean
    testID?: string
}

/**
 * La place du trait, en em, mesurée depuis le bas des mots. Le jour : la bande de `<Highlight>`
 * (de 56 % d'une ligne d'Outfit, ≈ 1,26 em, jusqu'à 0,06 em sous elle). La nuit : un trait fin
 * posé sous les lettres.
 */
const BAND = {
    day: { height: 0.62, bottom: highlighter.day.bottom },
    night: { height: 0.16, bottom: -0.2 },
} as const

export function WaitingText(props: WaitingTextProps) {
    const {
        children,
        size = "text-base",
        weight = "medium",
        color = "text-primary",
        numberOfLines = 1,
        align = "center",
        announce = true,
        testID,
    } = props
    const { theme } = useUnistyles()
    const night = useIsDarkTheme()
    const reduceMotion = useReduceMotion()
    // La largeur du trait est un état d'INTERFACE : le mot la mesure seul.
    const [width, setWidth] = useState(0)
    const reveal = useRef(new Animated.Value(0)).current
    const fade = useRef(new Animated.Value(1)).current
    const [x1, y1, x2, y2] = theme.motion.easing
    const { micro, standard, entree } = theme.motion

    useEffect(() => {
        if (reduceMotion) {
            reveal.setValue(1)
            fade.setValue(1)
            return
        }
        // Tant que la largeur n'est pas mesurée, rien ne se montre : pas de trait plein une image.
        if (width === 0) {
            fade.setValue(0)
            return
        }
        // react-native-web n'a pas de pilote natif ; y prétendre ne fait qu'avertir.
        const useNativeDriver = Platform.OS !== "web"
        const set = (value: Animated.Value, toValue: number) =>
            Animated.timing(value, { toValue, duration: 0, useNativeDriver })
        // Tracer le trait, le laisser, l'effacer, reprendre : un peu moins de deux secondes, le
        // souffle des objets de scène du livre (« Respirer », 1,7 s).
        const loop = Animated.loop(
            Animated.sequence([
                Animated.parallel([set(reveal, 0), set(fade, 1)]),
                Animated.timing(reveal, {
                    toValue: 1,
                    duration: entree * 2,
                    easing: Easing.bezier(x1, y1, x2, y2),
                    useNativeDriver,
                }),
                Animated.delay(standard * 2),
                Animated.timing(fade, { toValue: 0, duration: standard, useNativeDriver }),
                Animated.delay(micro),
            ])
        )
        loop.start()
        return () => loop.stop()
    }, [reveal, fade, width, reduceMotion, x1, y1, x2, y2, micro, standard, entree])

    const onLayout = (event: LayoutChangeEvent) => {
        const next = event.nativeEvent.layout.width
        setWidth((previous) => (previous === next ? previous : next))
    }

    const translateX = reveal.interpolate({ inputRange: [0, 1], outputRange: [-width, 0] })

    return (
        <View
            testID={testID}
            style={styles.word(size, align)}
            {...(announce
                ? {
                      accessible: true,
                      accessibilityRole: "progressbar" as const,
                      accessibilityLabel: children,
                      accessibilityState: { busy: true },
                      accessibilityLiveRegion: "polite" as const,
                  }
                : { importantForAccessibility: "no-hide-descendants" as const, "aria-hidden": true })}
        >
            <View style={styles.clip(size, night)} onLayout={onLayout} pointerEvents="none">
                <Animated.View style={[styles.fill, { opacity: fade, transform: [{ translateX }] }]} />
            </View>
            <Text size={size} weight={weight} color={color} center={align === "center"} numberOfLines={numberOfLines}>
                {children}
            </Text>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    word: (size: FontSizeKey, align: WaitingTextAlign) => ({
        alignSelf: alignMap[align],
        // Jamais plus large que son conteneur : une ligne trop longue se coupe au lieu de déborder.
        maxWidth: "100%" as const,
        paddingHorizontal: highlighter.day.padX * theme.fontSizes[size],
    }),
    /** La place du trait sous le bas des mots, et ce qui le rogne pendant qu'il se trace. */
    clip: (size: FontSizeKey, night: boolean) => {
        const em = theme.fontSizes[size]
        const band = night ? BAND.night : BAND.day
        return {
            position: "absolute" as const,
            left: highlighter.day.insetX * em,
            right: highlighter.day.insetX * em,
            bottom: band.bottom * em,
            height: band.height * em,
            borderRadius: highlighter.radius * em,
            overflow: "hidden" as const,
            transform: [{ rotate: `${highlighter.rotate}deg` }],
        }
    },
    fill: {
        ...RNStyleSheet.absoluteFillObject,
        backgroundColor: theme.colors.accent,
    },
}))
